<script setup lang="ts">
import { onMounted, ref } from 'vue';

import {
  AlgoliaVariant,
  BaseAlgoliaVariant,
  multiQuerySearch,
  PRODUCTS_INDEX,
  productSearchParams,
  tagFacetFilter,
} from '@/api/algolia';
import BannerSection from '@/components/base/layout/BannerSection.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import ProductRecommendations from '@/components/recommendations/ProductRecommendations.vue';
import { useSpacing } from '@/composables/useSpacing';
import { reportError } from '@/utils/reportError';

const props = defineProps<{
  fallbackReason?: Error;
  unexpected?: boolean;
}>();

if (props.unexpected) reportError(`Fallback to static Homepage due to: ${props.fallbackReason}`);

const { spacing } = useSpacing({ spacingDirection: 'bottom', spacingLevel: 'xl' });

interface AlgoliaRowConfig {
  algoliaTag: string;
  link: string;
  ruleContext: string;
  title: string;
}
interface ExpandedAlgoliaRow extends AlgoliaRowConfig {
  recommendations: AlgoliaVariant[];
}

const algoliaRows: AlgoliaRowConfig[] = [
  {
    algoliaTag: 'hprow1',
    link: '',
    ruleContext: 'Homepage_Row_1',
    title: 'Our Family’s Picks',
  },
  {
    algoliaTag: 'hprow2',
    link: '/nuts',
    ruleContext: 'Homepage_Row_2',
    title: 'Nuts',
  },
  {
    algoliaTag: 'hprow3',
    link: '/driedfruit',
    ruleContext: 'Homepage_Row_3',
    title: 'Dried Fruit',
  },
  {
    algoliaTag: 'hprow4',
    link: '/chocolatessweets',
    ruleContext: 'Homepage_Row_4',
    title: 'Chocolates & Sweets',
  },
  {
    algoliaTag: 'hprow5',
    link: '/snacks',
    ruleContext: 'Homepage_Row_5',
    title: 'Snacks',
  },
  {
    algoliaTag: 'hprow6',
    link: '/cookingbaking',
    ruleContext: 'Homepage_Row_6',
    title: 'Cooking & Baking',
  },
  {
    algoliaTag: 'hprow7',
    link: '/coffeesteas',
    ruleContext: 'Homepage_Row_7',
    title: 'Coffees & Tea',
  },
];

const expandedAlgoliaRows = ref<ExpandedAlgoliaRow[]>([]);
const loadHomepageExperiences = async () => {
  const searchResults = await multiQuerySearch<BaseAlgoliaVariant[]>(
    ...algoliaRows.map((row) => ({
      indexName: PRODUCTS_INDEX,
      ...productSearchParams({
        analyticsTags: ['Static Homepage / 404 Fallback'],
        facetFilters: [tagFacetFilter(row.algoliaTag)],
        ruleContexts: [row.ruleContext],
      }),
    })),
  );

  expandedAlgoliaRows.value = algoliaRows.map((row, i) => {
    const recommendations = searchResults[i].hits;
    return {
      ...row,
      recommendations,
    };
  });
};
onMounted(() => {
  loadHomepageExperiences();
});
</script>

<template>
  <div class="container px-4 mx-auto" :class="spacing">
    <BannerSection
      link="/nuts/roasted/"
      headline="Made Fresh Since 1929"
      headlineColor="black"
      textColor="black"
      text="Shop our roasted and salted nuts for every craving"
      backgroundColor="nuts-stone-100"
      foregroundColor="nuts-stone-100"
      bgDesktopImg="https://nuts.com/images/dy/2022/Homepage_Hero_Roasted_Nuts.275ea1eb.jpg"
      bgMobileImg="https://nuts.com/images/dy/2022/Homepage_Hero_Roasted_Nuts_Mobile.fc9bcf33.jpg"
      data-promo="1"
      data-promo-name="HP Hero"
      data-promo-creative="Evergreen Fallback Banner"
    >
      <ThemedButton theme="gray" size="large"> Shop Now </ThemedButton>
    </BannerSection>
    <div v-for="row in expandedAlgoliaRows" :key="row.algoliaTag">
      <ProductRecommendations
        enableAddToCart
        :link="row.link"
        :list="row.recommendations"
        spacingDirection="top"
        spacingLevel="md"
        :title="row.title"
        trackingEventLocation="HOMEPAGE"
      />
    </div>
  </div>
</template>
